import { useState, useCallback, useRef } from "react";
import { ClientFacingWorkflowStatus } from "@src/types.generated";
import { gql } from "@apollo/client";
import { usePollInterval } from "@src/lib/hooks/usePollInterval";
import {
  useClientFacingWorkflowQuery,
  ClientFacingWorkflowFragment,
} from "./useTrackClientFacingWorkflow.generated";

type UseTrackClientFacingWorkflowOptions = {
  onCompleted?: (cfw: ClientFacingWorkflowFragment) => void;
  onError?: (cfw: ClientFacingWorkflowFragment) => void;
};

type UseTrackClientFacingWorkflowReturn = {
  trackClientFacingWorkflow: (cfw: ClientFacingWorkflowFragment) => void;
  clientFacingWorkflow: ClientFacingWorkflowFragment | null;
  isTracking: boolean;
  stopTracking: () => void;
};

export default function useTrackClientFacingWorkflow(
  options: UseTrackClientFacingWorkflowOptions = {},
): UseTrackClientFacingWorkflowReturn {
  const { onCompleted, onError } = options;
  const [trackedWorkflowId, setTrackedWorkflowId] = useState<string | null>(
    null,
  );
  const [isTracking, setIsTracking] = useState(false);
  const callbacksRef = useRef({ onCompleted, onError });
  callbacksRef.current = { onCompleted, onError };
  const pollInterval = usePollInterval(1000);
  const { data, stopPolling } = useClientFacingWorkflowQuery({
    variables: { id: trackedWorkflowId! },
    skip: !trackedWorkflowId || !isTracking,
    pollInterval,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
    onCompleted: (data) => {
      const workflow = data.ClientFacingWorkflow;
      if (!workflow) return;
      if (workflow.status === ClientFacingWorkflowStatus.Completed) {
        setIsTracking(false);
        stopPolling();
        callbacksRef.current.onCompleted?.(workflow);
      } else if (workflow.status === ClientFacingWorkflowStatus.Failed) {
        setIsTracking(false);
        stopPolling();
        callbacksRef.current.onError?.(workflow);
      }
    },
  });

  const trackClientFacingWorkflow = useCallback(
    (cfw: ClientFacingWorkflowFragment) => {
      if (cfw.status === ClientFacingWorkflowStatus.Completed) {
        callbacksRef.current.onCompleted?.(cfw);
        return;
      } else if (cfw.status === ClientFacingWorkflowStatus.Failed) {
        callbacksRef.current.onError?.(cfw);
        return;
      }
      setTrackedWorkflowId(cfw.id);
      setIsTracking(true);
    },
    [],
  );

  const stopTracking = useCallback(() => {
    setIsTracking(false);
    setTrackedWorkflowId(null);
    stopPolling();
  }, [stopPolling]);

  return {
    trackClientFacingWorkflow,
    clientFacingWorkflow: data?.ClientFacingWorkflow || null,
    isTracking,
    stopTracking,
  };
}

gql`
  fragment ClientFacingWorkflow on ClientFacingWorkflow {
    id
    type
    status
    createdAt
    result {
      id
      libraryItem {
        id
        name {
          en
        }
        course {
          ...CreateAICourseResult
        }
        skill {
          ...CreateAiOutlineResult
        }
      }
    }
  }

  query ClientFacingWorkflow($id: UUID!) {
    ClientFacingWorkflow(id: $id) {
      ...ClientFacingWorkflow
    }
  }
`;
